import React, { useCallback, useEffect, useState } from 'react';
import { apiFetch, parseJsonSafe } from '../../utils/api';

const STATUS_OPTIONS = [
  { value: 'activa', label: 'Activa' },
  { value: 'consumida', label: 'Consumida' },
  { value: 'suspendida', label: 'Suspendida' },
  { value: 'borrador', label: 'Borrador' }
];
const statusBadge = (s) => ({
  activa: 'bg-success', consumida: 'bg-secondary', suspendida: 'bg-warning text-dark', borrador: 'bg-light text-dark'
}[s] || 'bg-light text-dark');

const todayStr = () => {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};
const fmtHoras = (minutos) => {
  const m = Math.round(Number(minutos) || 0);
  const neg = m < 0;
  const a = Math.abs(m);
  const h = Math.floor(a / 60);
  const min = a % 60;
  return `${neg ? '-' : ''}${h}h${min ? ` ${min}min` : ''}`;
};
const fmtDate = (d) => {
  if (!d) return '-';
  const s = String(d).slice(0, 10);
  const [y, m, day] = s.split('-');
  return (y && m && day) ? `${day}/${m}/${y}` : s;
};

const emptyPackForm = { label: '', total_hours: '', hourly_rate: '', fecha_inicio: todayStr(), status: 'activa', referencia: '' };

const ClientTimePacksTab = ({ clientId, onChanged }) => {
  const token = localStorage.getItem('token');
  const [packs, setPacks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPackForm, setShowPackForm] = useState(false);
  const [editingPackId, setEditingPackId] = useState(null);
  const [packForm, setPackForm] = useState(emptyPackForm);
  const [savingPack, setSavingPack] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [entries, setEntries] = useState([]);
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [entryForm, setEntryForm] = useState({ fecha: todayStr(), horas: '', descripcion: '', user_id: '' });
  const [savingEntry, setSavingEntry] = useState(false);
  const [editingEntryId, setEditingEntryId] = useState(null);

  const handleUnauthorized = () => {
    localStorage.removeItem('token');
    window.location.href = '/login';
  };

  const fetchPacks = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiFetch(`/api/time-packs?client_id=${clientId}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error((data && data.message) || 'Error cargando bolsas de horas');
      setPacks(Array.isArray(data) ? data : []);
      if (onChanged) onChanged();
    } catch (err) {
      setPacks([]);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [clientId, token, onChanged]);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await apiFetch('/api/users', { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.ok && Array.isArray(data)) setUsers(data);
    } catch (_e) { /* noop */ }
  }, [token]);

  useEffect(() => { fetchPacks(); fetchUsers(); }, [fetchPacks, fetchUsers]);

  const openNewPack = () => { setEditingPackId(null); setPackForm(emptyPackForm); setShowPackForm(true); };
  const openEditPack = (p) => {
    setEditingPackId(p.id);
    setPackForm({
      label: p.label || '', total_hours: p.total_hours ?? '', hourly_rate: p.hourly_rate ?? '',
      fecha_inicio: p.fecha_inicio ? String(p.fecha_inicio).slice(0, 10) : '', status: p.status || 'activa',
      referencia: p.referencia || ''
    });
    setShowPackForm(true);
  };

  const savePack = async () => {
    if (!packForm.label.trim() || !(Number(packForm.total_hours) > 0)) {
      setError('Nombre y horas contratadas (>0) son obligatorios');
      return;
    }
    setSavingPack(true);
    setError('');
    try {
      const isEdit = !!editingPackId;
      const url = isEdit ? `/api/time-packs/${editingPackId}` : '/api/time-packs';
      const body = {
        client_id: clientId,
        label: packForm.label.trim(),
        total_hours: Number(packForm.total_hours),
        hourly_rate: packForm.hourly_rate === '' ? null : Number(packForm.hourly_rate),
        status: packForm.status,
        fecha_inicio: packForm.fecha_inicio || null,
        referencia: packForm.referencia.trim() || null
      };
      const res = await apiFetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body)
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error((data && data.message) || 'No se pudo guardar la bolsa');
      setShowPackForm(false);
      setEditingPackId(null);
      await fetchPacks();
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingPack(false);
    }
  };

  const deletePack = async (id) => {
    if (!window.confirm('¿Eliminar esta bolsa y todas sus intervenciones?')) return;
    try {
      const res = await apiFetch(`/api/time-packs/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) { const d = await parseJsonSafe(res); throw new Error((d && d.message) || 'No se pudo eliminar'); }
      if (expandedId === id) setExpandedId(null);
      await fetchPacks();
    } catch (err) { alert(err.message); }
  };

  const loadEntries = async (packId) => {
    setEntriesLoading(true);
    try {
      const res = await apiFetch(`/api/time-packs/${packId}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error((data && data.message) || 'Error cargando intervenciones');
      setEntries(Array.isArray(data.entries) ? data.entries : []);
    } catch (err) {
      setEntries([]);
      alert(err.message);
    } finally {
      setEntriesLoading(false);
    }
  };

  const toggleExpand = (packId) => {
    setEditingEntryId(null);
    if (expandedId === packId) { setExpandedId(null); return; }
    setExpandedId(packId);
    setEntryForm({ fecha: todayStr(), horas: '', descripcion: '', user_id: '' });
    loadEntries(packId);
  };

  const startEditEntry = (e) => {
    setEditingEntryId(e.id);
    setEntryForm({
      fecha: e.fecha ? String(e.fecha).slice(0, 10) : todayStr(),
      horas: e.minutos != null ? String(Math.round((Number(e.minutos) / 60) * 100) / 100) : '',
      descripcion: e.descripcion || '',
      user_id: e.user_id || ''
    });
  };
  const cancelEditEntry = () => {
    setEditingEntryId(null);
    setEntryForm({ fecha: todayStr(), horas: '', descripcion: '', user_id: '' });
  };

  const submitEntry = async (packId) => {
    if (!(Number(entryForm.horas) > 0)) { alert('Indica las horas de la intervención'); return; }
    setSavingEntry(true);
    try {
      const isEdit = !!editingEntryId;
      const url = isEdit ? `/api/time-packs/entries/${editingEntryId}` : `/api/time-packs/${packId}/entries`;
      const res = await apiFetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          fecha: entryForm.fecha || null,
          horas: Number(entryForm.horas),
          descripcion: entryForm.descripcion || null,
          user_id: entryForm.user_id || null
        })
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error((data && data.message) || 'No se pudo guardar la intervención');
      setEditingEntryId(null);
      setEntryForm({ fecha: todayStr(), horas: '', descripcion: '', user_id: '' });
      await loadEntries(packId);
      await fetchPacks();
    } catch (err) {
      alert(err.message);
    } finally {
      setSavingEntry(false);
    }
  };

  const deleteEntry = async (packId, entryId) => {
    if (!window.confirm('¿Eliminar esta intervención?')) return;
    try {
      const res = await apiFetch(`/api/time-packs/entries/${entryId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) { const d = await parseJsonSafe(res); throw new Error((d && d.message) || 'No se pudo eliminar'); }
      await loadEntries(packId);
      await fetchPacks();
    } catch (err) { alert(err.message); }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h5 className="mb-0">Bolsas de horas</h5>
          <div className="text-muted small">Bonos de horas de mantenimiento/soporte y sus intervenciones.</div>
        </div>
        {!showPackForm && <button className="btn btn-primary btn-sm" type="button" onClick={openNewPack}>+ Nueva bolsa</button>}
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {showPackForm && (
        <div className="card shadow-sm mb-3 border-primary">
          <div className="card-body">
            <h6 className="mb-3">{editingPackId ? 'Editar bolsa' : 'Nueva bolsa de horas'}</h6>
            <div className="row g-2">
              <div className="col-md-5">
                <label className="form-label small">Nombre *</label>
                <input className="form-control" value={packForm.label} onChange={(e) => setPackForm((p) => ({ ...p, label: e.target.value }))} placeholder="Ej. Bolsa de 10 horas" />
              </div>
              <div className="col-md-2">
                <label className="form-label small">Horas *</label>
                <input type="number" min="0" step="0.5" className="form-control" value={packForm.total_hours} onChange={(e) => setPackForm((p) => ({ ...p, total_hours: e.target.value }))} />
              </div>
              <div className="col-md-2">
                <label className="form-label small">€/hora</label>
                <input type="number" min="0" step="0.01" className="form-control" value={packForm.hourly_rate} onChange={(e) => setPackForm((p) => ({ ...p, hourly_rate: e.target.value }))} />
              </div>
              <div className="col-md-3">
                <label className="form-label small">Estado</label>
                <select className="form-select" value={packForm.status} onChange={(e) => setPackForm((p) => ({ ...p, status: e.target.value }))}>
                  {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label small">Fecha inicio</label>
                <input type="date" className="form-control" value={packForm.fecha_inicio} onChange={(e) => setPackForm((p) => ({ ...p, fecha_inicio: e.target.value }))} />
              </div>
              <div className="col-md-3">
                <label className="form-label small">Referencia</label>
                <input className="form-control" value={packForm.referencia} onChange={(e) => setPackForm((p) => ({ ...p, referencia: e.target.value }))} placeholder="Auto (BH000-…)" />
              </div>
            </div>
            <div className="d-flex justify-content-end gap-2 mt-3">
              <button className="btn btn-outline-secondary" type="button" onClick={() => { setShowPackForm(false); setEditingPackId(null); }}>Cancelar</button>
              <button className="btn btn-primary" type="button" disabled={savingPack} onClick={savePack}>{savingPack ? 'Guardando...' : 'Guardar'}</button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-muted">Cargando...</p>
      ) : packs.length === 0 ? (
        !showPackForm && <div className="text-center text-muted py-4 border rounded bg-light">Sin bolsas de horas. Pulsa "Nueva bolsa" para crear una.</div>
      ) : (
        packs.map((p) => {
          const totalMin = Math.round(Number(p.total_hours) * 60);
          const consumido = Number(p.minutos_consumidos) || 0;
          const restante = totalMin - consumido;
          const pct = totalMin > 0 ? Math.min(100, Math.round((consumido / totalMin) * 100)) : 0;
          const over = restante < 0;
          return (
            <div key={p.id} className="card shadow-sm mb-3">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <h6 className="mb-1 fw-bold">
                      {p.referencia && <span className="text-primary me-2">{p.referencia}</span>}
                      {p.label} <span className={`badge ${statusBadge(p.status)} ms-1`}>{p.status}</span>
                    </h6>
                    <div className="text-muted small">
                      Inicio: {fmtDate(p.fecha_inicio)}{p.hourly_rate ? ` · ${Number(p.hourly_rate).toLocaleString('es-ES')} €/h` : ''}
                    </div>
                  </div>
                  <div className="btn-group btn-group-sm">
                    <button className="btn btn-outline-secondary" type="button" onClick={() => openEditPack(p)}>Editar</button>
                    <button className="btn btn-outline-danger" type="button" onClick={() => deletePack(p.id)}>Eliminar</button>
                  </div>
                </div>

                <div className="row text-center my-2 g-2">
                  <div className="col-4"><div className="small text-muted">Contratadas</div><div className="fw-bold">{fmtHoras(totalMin)}</div></div>
                  <div className="col-4"><div className="small text-muted">Consumidas</div><div className="fw-bold text-warning">{fmtHoras(consumido)}</div></div>
                  <div className="col-4"><div className="small text-muted">Restantes</div><div className={`fw-bold ${over ? 'text-danger' : 'text-success'}`}>{fmtHoras(restante)}</div></div>
                </div>
                <div className="progress" style={{ height: 8 }}>
                  <div className={`progress-bar ${over ? 'bg-danger' : 'bg-success'}`} role="progressbar" style={{ width: `${pct}%` }} aria-valuenow={pct} aria-valuemin="0" aria-valuemax="100"></div>
                </div>

                <div className="mt-2">
                  <button className="btn btn-sm btn-link p-0" type="button" onClick={() => toggleExpand(p.id)}>
                    {expandedId === p.id ? '▾ Ocultar intervenciones' : `▸ Ver/añadir intervenciones (${p.num_entradas || 0})`}
                  </button>
                </div>

                {expandedId === p.id && (
                  <div className="border-top mt-2 pt-2">
                    {/* alta de intervención */}
                    <div className="row g-2 align-items-end mb-2">
                      <div className="col-md-2">
                        <label className="form-label small mb-0">Fecha</label>
                        <input type="date" className="form-control form-control-sm" value={entryForm.fecha} onChange={(e) => setEntryForm((f) => ({ ...f, fecha: e.target.value }))} />
                      </div>
                      <div className="col-md-2">
                        <label className="form-label small mb-0">Horas</label>
                        <input type="number" min="0" step="0.25" className="form-control form-control-sm" value={entryForm.horas} onChange={(e) => setEntryForm((f) => ({ ...f, horas: e.target.value }))} />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label small mb-0">Técnico</label>
                        <select className="form-select form-select-sm" value={entryForm.user_id} onChange={(e) => setEntryForm((f) => ({ ...f, user_id: e.target.value }))}>
                          <option value="">(yo)</option>
                          {users.map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
                        </select>
                      </div>
                      <div className="col-md-3">
                        <label className="form-label small mb-0">Descripción</label>
                        <input className="form-control form-control-sm" value={entryForm.descripcion} onChange={(e) => setEntryForm((f) => ({ ...f, descripcion: e.target.value }))} placeholder="Trabajo realizado" />
                      </div>
                      <div className="col-md-2 d-grid gap-1">
                        <button className="btn btn-sm btn-primary" type="button" disabled={savingEntry} onClick={() => submitEntry(p.id)}>{savingEntry ? '...' : (editingEntryId ? 'Guardar' : 'Añadir')}</button>
                        {editingEntryId && <button className="btn btn-sm btn-outline-secondary" type="button" onClick={cancelEditEntry}>Cancelar</button>}
                      </div>
                    </div>

                    {entriesLoading ? (
                      <div className="text-muted small">Cargando intervenciones...</div>
                    ) : entries.length === 0 ? (
                      <div className="text-muted small">Sin intervenciones registradas.</div>
                    ) : (
                      <div className="table-responsive">
                        <table className="table table-sm align-middle mb-0">
                          <thead><tr><th>Fecha</th><th>Horas</th><th>Técnico</th><th>Descripción</th><th></th></tr></thead>
                          <tbody>
                            {entries.map((e) => (
                              <tr key={e.id} className={editingEntryId === e.id ? 'table-warning' : ''}>
                                <td>{fmtDate(e.fecha)}</td>
                                <td>{fmtHoras(e.minutos)}</td>
                                <td>{e.user_name || e.user_email || '-'}</td>
                                <td>{e.descripcion || '-'}</td>
                                <td className="text-end">
                                  <div className="btn-group btn-group-sm">
                                    <button className="btn btn-outline-secondary" type="button" onClick={() => startEditEntry(e)}>Editar</button>
                                    <button className="btn btn-outline-danger" type="button" onClick={() => deleteEntry(p.id, e.id)}>Eliminar</button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};

export default ClientTimePacksTab;
